import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { PERMISSIONS } from '../constants/permissions';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Collapse,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inbox as InboxIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  Biotech as BiotechIcon,
  Medication as MedicationIcon,
  CleaningServices as CleaningServicesIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Description as DescriptionIcon,
  Grading as GradingIcon,
  FactCheck as FactCheckIcon,
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  Policy as PolicyIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  MarkEmailRead as EmailTemplateIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
  Shield as ShieldIcon,
  Email as EmailIcon,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapHorizIcon,
  Check as CheckIcon,
  TaskAlt as TaskAltIcon,
  Checklist as ChecklistIcon,
  HelpOutline as HelpOutlineIcon,
  Visibility as VisibilityIcon,
  Badge as BadgeIcon,
  RuleFolder as RuleFolderIcon,
  Opacity as OpacityIcon,
  LibraryBooks as LibraryBooksIcon,
  Rule as RuleIcon,
  FormatListNumbered as RankingIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import NotificationBell from './NotificationBell';
import { ONBOARDING_EDIT_PERMISSIONS, ONBOARDING_STATES } from '../constants/onboarding';
import * as onboardingApi from '../services/onboardingApi';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 68;

const transition = (theme) =>
  theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

// `permission` = the permission the page's API requires; items without one are open
// to every authenticated member. Groups hide automatically when all children are hidden.
const tenantNavItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Inbox', icon: <InboxIcon />, path: '/inbox' },
  {
    text: 'Registry',
    icon: <InventoryIcon />,
    children: [
      { text: 'Equipment', icon: <ScienceIcon />, path: '/equipment', permission: PERMISSIONS.EQUIPMENT_VIEW },
      { text: 'HBELs', icon: <MedicationIcon />, path: '/hbels', permission: PERMISSIONS.HBELS_VIEW },
      { text: 'Products', icon: <BiotechIcon />, path: '/products', permission: PERMISSIONS.PRODUCTS_VIEW },
      { text: 'Cleaning Agent/Disinfectant', icon: <CleaningServicesIcon />, path: '/cleaning-agents', permission: PERMISSIONS.CLEANING_AGENTS_VIEW },
      { text: 'Analytical Methods', icon: <AnalyticsIcon />, path: '/analytical-methods', permission: PERMISSIONS.ANALYTICAL_METHODS_VIEW },
      { text: 'Documents', icon: <DescriptionIcon />, path: '/documents', permission: PERMISSIONS.DOCUMENTS_VIEW },
    ],
  },
  {
    text: 'Validation',
    icon: <AssessmentIcon />,
    children: [
      { text: 'New Product Assessment', icon: <GradingIcon />, path: '/new-product-assessment', permission: PERMISSIONS.NPA_VIEW },
      { text: 'FMECA Assessment', icon: <WarningIcon />, path: '/fmeca-assessment', permission: PERMISSIONS.FMECA_VIEW },
      { text: 'Validation/Verification', icon: <FactCheckIcon />, path: '/validation-protocols', permission: PERMISSIONS.PROTOCOL_VIEW },
      { text: 'Risk Register', icon: <PolicyIcon />, path: '/risk-register', permission: PERMISSIONS.RISK_REGISTER_VIEW },
      { text: 'My Actions', icon: <TaskAltIcon />, path: '/my-actions' },
      { text: 'Limit Calculator', icon: <CalculateIcon />, path: '/limit-calculator' },
    ],
  },
  {
    text: 'Visual Inspection',
    icon: <VisibilityIcon />,
    children: [
      { text: 'Inspector Register', icon: <BadgeIcon />, path: '/visual-inspection/inspectors', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'Qualification Sessions', icon: <RuleFolderIcon />, path: '/visual-inspection/qualifications', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'Ranking Studies', icon: <RankingIcon />, path: '/visual-inspection/ranking-studies', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'VRL Studies', icon: <OpacityIcon />, path: '/visual-inspection/vrl-studies', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'VRL Library', icon: <LibraryBooksIcon />, path: '/visual-inspection/vrl-library', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'VI Suitability', icon: <RuleIcon />, path: '/visual-inspection/suitability', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
      { text: 'VI Trends', icon: <TimelineIcon />, path: '/visual-inspection/trends', permission: PERMISSIONS.VISUAL_INSPECTION_VIEW },
    ],
  },
  {
    text: 'Admin',
    icon: <SettingsIcon />,
    children: [
      { text: 'Email Templates', icon: <EmailTemplateIcon />, path: '/email-templates', permission: PERMISSIONS.EMAIL_TEMPLATE_EDIT },
      { text: 'Team', icon: <GroupIcon />, path: '/team', permission: PERMISSIONS.TEAM_EDIT },
      { text: 'Roles', icon: <SecurityIcon />, path: '/roles', permission: PERMISSIONS.ROLES_EDIT },
      { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit-log', permission: PERMISSIONS.AUDIT_VIEW },
      { text: 'Settings', icon: <SettingsIcon />, path: '/org-settings', permission: PERMISSIONS.SETTINGS_EDIT },
    ],
  },
];

const hostNavItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Organisations', icon: <BusinessIcon />, path: '/organisations' },
  { text: 'Team', icon: <GroupIcon />, path: '/host-team' },
  { text: 'Email Templates', icon: <EmailTemplateIcon />, path: '/host-email-templates' },
  { text: 'Email Log', icon: <EmailIcon />, path: '/email-log' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

export function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openGroups, setOpenGroups] = useState({ Registry: true, Validation: true, 'Visual Inspection': true, Admin: true });
  const [anchorEl, setAnchorEl] = useState(null);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [orgAnchorEl, setOrgAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, switchOrg, hasPermission } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const isHost = user?.isHost === true;
  // Hide items whose permission the user lacks; drop a group when all its children are hidden.
  const navItems = useMemo(() => {
    if (isHost) return hostNavItems;
    return tenantNavItems
      .map((item) => item.children
        ? { ...item, children: item.children.filter((c) => !c.permission || hasPermission(c.permission)) }
        : item)
      .filter((item) => item.children
        ? item.children.length > 0
        : !item.permission || hasPermission(item.permission));
  }, [isHost, hasPermission]);
  const memberships = user?.memberships || [];
  const canActAsHost = user?.canActAsHost === true;
  // Host counts as one selectable context alongside each org membership.
  const selectableCount = (canActAsHost ? 1 : 0) + memberships.length;
  const showOrgSwitcher = selectableCount > 1;

  // organisationId null = switch to the host (platform) context.
  const handleSwitchContext = async (organisationId) => {
    const targetIsHost = organisationId == null;
    const alreadyActive = targetIsHost ? isHost : (!isHost && organisationId === user?.organisationId);
    if (alreadyActive) {
      setOrgAnchorEl(null);
      return;
    }
    setSwitching(true);
    const result = await switchOrg(organisationId);
    setSwitching(false);
    setOrgAnchorEl(null);
    if (result.success) {
      navigate('/');
    }
  };

  // Re-expands a minimised/dismissed checklist and shows it on the Dashboard; the state token
  // makes the Dashboard reload its status even when it is already the current page.
  const showHelpMenu = !isHost && ONBOARDING_EDIT_PERMISSIONS.some(hasPermission);
  const reopenGettingStarted = async () => {
    setHelpAnchorEl(null);
    await onboardingApi.setState(ONBOARDING_STATES.EXPANDED);
    navigate('/', { state: { onboardingRefresh: Date.now() } });
  };

  const currentWidth = sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED;

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    if (item.children) {
      // When collapsed, show children as flat icon-only items with tooltips
      if (!sidebarOpen) {
        return (
          <React.Fragment key={item.text}>
            {item.children.map((child) => (
              <Tooltip key={child.text} title={child.text} placement="right" arrow>
                <ListItemButton
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    justifyContent: 'center',
                    px: 1.5,
                    bgcolor: isActive(child.path) ? 'primary.main' : 'transparent',
                    color: isActive(child.path) ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: isActive(child.path) ? 'primary.dark' : 'action.hover',
                    },
                  }}
                  onClick={() => navigate(child.path)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: isActive(child.path) ? 'primary.contrastText' : 'inherit',
                    }}
                  >
                    {child.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ))}
          </React.Fragment>
        );
      }

      // Expanded: show collapsible group
      return (
        <React.Fragment key={item.text}>
          <ListItemButton
            onClick={() => setOpenGroups((prev) => ({ ...prev, [item.text]: !prev[item.text] }))}
            sx={{ borderRadius: 1, mb: 0.25 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {openGroups[item.text] ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openGroups[item.text]} timeout="auto" unmountOnExit>
            <List component="div" dense disablePadding>
              {item.children.map((child) => (
                <ListItemButton
                  key={child.text}
                  sx={{
                    pl: 4,
                    borderRadius: 1,
                    mb: 0.25,
                    bgcolor: isActive(child.path) ? 'primary.main' : 'transparent',
                    color: isActive(child.path) ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: isActive(child.path) ? 'primary.dark' : 'action.hover',
                    },
                  }}
                  onClick={() => navigate(child.path)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive(child.path) ? 'primary.contrastText' : 'inherit',
                    }}
                  >
                    {child.icon}
                  </ListItemIcon>
                  <ListItemText primary={child.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    // Regular nav item
    if (!sidebarOpen) {
      return (
        <Tooltip key={item.text} title={item.text} placement="right" arrow>
          <ListItemButton
            sx={{
              borderRadius: 1,
              mb: 0.25,
              justifyContent: 'center',
              px: 1.5,
              bgcolor: isActive(item.path) ? 'primary.main' : 'transparent',
              color: isActive(item.path) ? 'primary.contrastText' : 'text.primary',
              '&:hover': {
                bgcolor: isActive(item.path) ? 'primary.dark' : 'action.hover',
              },
            }}
            onClick={() => navigate(item.path)}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                color: isActive(item.path) ? 'primary.contrastText' : 'inherit',
              }}
            >
              {item.icon}
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      );
    }

    return (
      <ListItemButton
        key={item.text}
        sx={{
          borderRadius: 1,
          mb: 0.25,
          bgcolor: isActive(item.path) ? 'primary.main' : 'transparent',
          color: isActive(item.path) ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            bgcolor: isActive(item.path) ? 'primary.dark' : 'action.hover',
          },
        }}
        onClick={() => navigate(item.path)}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,
            color: isActive(item.path) ? 'primary.contrastText' : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    );
  };

  const drawerContent = (expanded) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ justifyContent: 'space-between', gap: 1, minWidth: 0, px: expanded ? 2 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          {isHost
            ? <BusinessIcon color="primary" sx={{ flexShrink: 0 }} />
            : <ScienceIcon color="primary" sx={{ flexShrink: 0 }} />}
          {expanded && (
            <Tooltip title={isHost ? 'Host Admin' : (user?.organisationName || 'CleanVal')} arrow>
              <Typography variant="h6" noWrap color="primary.main" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isHost ? 'Host Admin' : (user?.organisationName || 'CleanVal')}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Tooltip title={sidebarOpen ? 'Collapse' : 'Expand'} placement="right" arrow>
          <IconButton
            onClick={handleSidebarToggle}
            sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}
          >
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List component="nav" dense sx={{ px: 1, flexGrow: 1, overflow: 'auto' }}>
        {navItems.map((item) => renderNavItem(item))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${currentWidth}px)` },
          ml: { md: `${currentWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          transition: (theme) => transition(theme),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {showOrgSwitcher && (
            <>
              <Tooltip title="Switch context" arrow>
                <Button
                  onClick={(e) => setOrgAnchorEl(e.currentTarget)}
                  disabled={switching}
                  startIcon={isHost ? <ShieldIcon /> : <BusinessIcon />}
                  endIcon={<SwapHorizIcon />}
                  sx={{
                    color: 'text.primary',
                    textTransform: 'none',
                    mr: 1,
                    maxWidth: 240,
                    '& .MuiButton-startIcon': { color: 'primary.main' },
                  }}
                >
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {isHost ? 'Host Administration' : user?.organisationName}
                  </Typography>
                </Button>
              </Tooltip>
              <Menu
                anchorEl={orgAnchorEl}
                open={Boolean(orgAnchorEl)}
                onClose={() => setOrgAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {canActAsHost && (
                  <MenuItem
                    onClick={() => handleSwitchContext(null)}
                    selected={isHost}
                  >
                    <ListItemIcon>
                      {isHost
                        ? <CheckIcon fontSize="small" color="primary" />
                        : <ShieldIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary="Host Administration" secondary="Platform" />
                  </MenuItem>
                )}
                {memberships.map((m) => (
                  <MenuItem
                    key={m.organisationId}
                    onClick={() => handleSwitchContext(m.organisationId)}
                    selected={!isHost && m.organisationId === user?.organisationId}
                  >
                    <ListItemIcon>
                      {!isHost && m.organisationId === user?.organisationId
                        ? <CheckIcon fontSize="small" color="primary" />
                        : <BusinessIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={m.organisationName}
                      secondary={m.role}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {user && !isHost && (
            <Box sx={{ mr: 0.5 }}><NotificationBell /></Box>
          )}
          {showHelpMenu && (
            <>
              <Tooltip title="Help" arrow>
                <IconButton
                  aria-label="Help"
                  onClick={(e) => setHelpAnchorEl(e.currentTarget)}
                  sx={{ color: 'text.secondary', mr: 0.5 }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={helpAnchorEl}
                open={Boolean(helpAnchorEl)}
                onClose={() => setHelpAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={reopenGettingStarted}>
                  <ListItemIcon><ChecklistIcon fontSize="small" /></ListItemIcon>
                  Getting started checklist
                </MenuItem>
              </Menu>
            </>
          )}
          <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'} arrow>
            <IconButton onClick={toggleTheme} sx={{ color: 'text.secondary', mr: 1 }}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          {user && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {user.firstName} {user.lastName}
            </Typography>
          )}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); logout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: currentWidth },
          flexShrink: { md: 0 },
          transition: (theme) => transition(theme),
        }}
      >
        {/* Mobile drawer — always full width */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerContent(true)}
        </Drawer>
        {/* Desktop drawer — collapsible */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentWidth,
              transition: (theme) => transition(theme),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawerContent(sidebarOpen)}
        </Drawer>
      </Box>

      {/* The content area — not the window — owns the vertical scrollbar, so pages
          can size themselves to the viewport and scroll their own grids instead. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${currentWidth}px)` },
          maxWidth: { md: `calc(100% - ${currentWidth}px)` },
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          boxSizing: 'border-box',
          overflow: 'hidden',
          transition: (theme) => transition(theme),
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, minHeight: 0, p: 3, overflowY: 'auto', overflowX: 'hidden' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
